import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ResultsPublication } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, StatCard, StatusBadge } from '../../ui/components';

const COMMISSIONER_ROLES = ['ELECTORAL_COMMISSIONER'];
const RETURNING_ROLES = ['RETURNING_OFFICER'];

/** GET /results/:id — officer view of a certified/published result, including its signed payload. */
export function ResultDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isCommissioner = roles.some((r) => COMMISSIONER_ROLES.includes(r));
  const isReturningOfficer = roles.some((r) => RETURNING_ROLES.includes(r));
  const resultQ = useApiQuery<ResultsPublication>(id ? `/api/v1/m39/results/${id}` : null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (resultQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading result…</div>;
  if (resultQ.error) return <ErrorNote message={resultQ.error} />;
  const r = resultQ.data;
  if (!r) return null;

  const canPublish = r.status === 'CERTIFIED' && (r.scope === 'STATION' ? isReturningOfficer || isCommissioner : isCommissioner);

  const publish = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/v1/m39/results/${r.id}/publish`);
      resultQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Publish failed');
    } finally {
      setBusy(false);
    }
  };

  const payload = r.payloadJson as
    | {
        candidates?: Array<{ nominationId: string; candidateName?: string; votes: number }>;
        certifiedAt?: string;
      }
    | undefined;

  return (
    <div>
      <PageHeader title={`${r.scope} result — election ${r.electionId}`} actions={<StatusBadge status={r.status} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Registered voters" value={r.totalRegisteredVoters} />
        <StatCard label="Valid votes" value={r.totalValidVotes} tone="good" />
        <StatCard label="Rejected votes" value={r.totalRejectedVotes} />
        <StatCard label="Outcome" value={r.outcome ? <StatusBadge status={r.outcome} /> : '—'} />
      </div>

      {payload?.candidates && payload.candidates.length > 0 ? (
        <div className="card mb-6 p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Candidate votes</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Candidate</th>
                <th className="table-head text-right">Votes</th>
              </tr>
            </thead>
            <tbody>
              {payload.candidates
                .slice()
                .sort((a, b) => b.votes - a.votes)
                .map((c) => (
                  <tr key={c.nominationId} className={c.nominationId === r.winningNominationId ? 'bg-emerald-50' : undefined}>
                    <td className="table-cell font-medium">
                      {c.candidateName ?? c.nominationId}
                      {c.nominationId === r.winningNominationId ? <span className="ml-2 text-xs font-bold text-emerald-700">WINNER</span> : null}
                    </td>
                    <td className="table-cell text-right tabular-nums">{c.votes}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="card mb-6 p-5 text-sm">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Verifiable signed payload</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="field-label">Certified by</div>
            {r.certifiedBy ?? '—'}
          </div>
          <div>
            <div className="field-label">Certified at</div>
            {formatDateTime(r.certifiedAt)}
          </div>
          <div>
            <div className="field-label">Published at</div>
            {formatDateTime(r.publishedAt)}
          </div>
          <div>
            <div className="field-label">OpenGov notice</div>
            {r.openGovNoticeId ?? '—'}
          </div>
          <div className="col-span-2">
            <div className="field-label">Payload hash (SHA-256)</div>
            <span className="break-all font-mono text-xs">{r.payloadHash}</span>
          </div>
        </div>
      </div>

      {canPublish ? (
        <button className="btn-primary" onClick={publish} disabled={busy}>
          {busy ? 'Publishing…' : 'Publish to C11 OpenGov'}
        </button>
      ) : r.status === 'PUBLISHED' ? (
        <p className="text-xs text-emerald-700">Published — visible on the public results feed (Elections → Public Results Feed).</p>
      ) : null}
    </div>
  );
}
